import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Container, Paper } from '@mui/material';
import { keyframes } from '@emotion/react';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { initializeGTMTracking, trackCustomEvent, trackUserJourney, trackPurchaseEvent } from '../utils/gtm.utils';

// Declare FirstPromoter types
declare global {
  interface Window {
      fpr?: {
          q: any[];
          (action: string, params?: any): void;
      };
      dataLayer?: any[];
  }
}

// Loading messages to display sequentially
const loadingMessages = [
  "Getting Everything Ready for You...",
  "Preparing your data...",
  "Almost ready..."
];

// Subheadings to display with more detailed information
const subheadings = [
  "We're setting up your EasyWebinar dashboard. This will only take a moment.",
  "Optimizing your dashboard for peak performance.",
  "Preparing your tools for hosting powerful webinars."
];

// Keyframes for animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-20px);
  }
`;

const scaleIn = keyframes`
  0% {
    transform: scale(0.8);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.1);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0.8;
  }
`;

// Styled components
const AnimatedMessage = styled(Box)`
  animation: ${fadeIn} 0.5s ease forwards;
`;

const AnimatedLoader = styled(Box)`
  animation: ${scaleIn} 0.5s ease forwards;
`;

const PulseBox = styled(Box)`
  animation: ${pulse} 1.5s ease-in-out infinite;
`;

const LoadingDashboard = () => {
    const navigate = useNavigate();
    const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
    const [isAnimatingOut, setIsAnimatingOut] = useState(false);
    const [currentSubheadingIndex, setCurrentSubheadingIndex] = useState(0);
    const [isScriptLoaded, setIsScriptLoaded] = useState(false);

    useEffect(() => {
        // Initialize GTM tracking
        const urlParams = new URLSearchParams(window.location.search);
        const email = urlParams.get('ew_email');
        const planId = urlParams.get('planId');
        const sso = urlParams.get('sso');

        initializeGTMTracking('Loading Dashboard', {
            user_email: email,
            plan_id: planId,
            is_sso: sso === 'true'
        });

        // Track signup to loading transition
        if (email || planId || sso) {
            trackUserJourney('signup_to_loading_transition', {
                user_email: email,
                plan_id: planId,
                is_sso: sso === 'true'
            });
        }

        // Initialize FirstPromoter
        if (!window.fpr) {
            const fprFunction: { (action: string, params?: any): void; q: any[] } = function() {
                if (!window.fpr?.q) return;
                window.fpr.q[arguments[0] === 'set' ? 'unshift' : 'push'](arguments);
            };
            fprFunction.q = [];
            window.fpr = fprFunction;
        }

        // Initialize FirstPromoter with CID and click tracking
        if (window.fpr) {
            window.fpr('init', { cid: 'u1f6nw3h' });
            window.fpr('click');
        }

        // Load FirstPromoter script
        const fprScript = document.createElement('script');
        fprScript.src = 'https://cdn.firstpromoter.com/fpr.js';
        fprScript.async = true;
        fprScript.onload = () => {
            setIsScriptLoaded(true);
        };
        document.head.appendChild(fprScript);

        // Add referral tracking
        const referralScript = document.createElement('script');
        referralScript.innerHTML = `
            function getParam(param) {
                return new URLSearchParams(window.location.search).get(param);
            }
            if (window.fpr) {
                const email = getParam('ew_email');
                if (email) {
                    window.fpr('referral', { email });
                }
            }
        `;
        document.head.appendChild(referralScript);

        // Cleanup function
        return () => {
            // Remove FirstPromoter scripts
            const scripts = document.head.getElementsByTagName('script');
            Array.from(scripts).forEach(script => {
                if (script.src.includes('firstpromoter.com') || 
                    script.innerHTML.includes('fpr')) {
                    document.head.removeChild(script);
                }
            });

            // Clean up FirstPromoter
            if (window.fpr) {
                window.fpr.q = [];
                window.fpr = undefined;
            }
        };
    }, []);

    // Function to handle navigation based on SSO
    const handleNavigation = () => {
        // GTM tracking for loading completion
        const urlParams = new URLSearchParams(window.location.search);
        const email = urlParams.get('ew_email');
        const planId = urlParams.get('planId');
        const sso = urlParams.get('sso');

        trackCustomEvent('loading_dashboard_completed', {
            user_email: email,
            plan_id: planId,
            is_sso: sso === 'true',
            loading_duration: '9000ms' // Approximate duration
        });

        // Track GTM purchase event when loading is completed
        try {
            // Extract plan data from URL parameters
            const price = parseFloat(urlParams.get('price') || '0');
            const currency = urlParams.get('currency') || 'USD';
            const coupon = urlParams.get('coupon') || '';
            const planName = urlParams.get('plan_name') || planId || 'Unknown Plan';
            const customerType = urlParams.get('customer_type') || 'without_cc';
            
            console.log('📊 Purchase Event Data:', {
                price,
                currency,
                coupon,
                plan_name: planName,
                customer_type: customerType
            });
            
            trackPurchaseEvent({
                price: price,
                currency: currency,
                coupon: coupon,
                customer_type: customerType as 'with_cc' | 'without_cc',
                items: [
                    {
                        item_name: planName
                    }
                ]
            });
        } catch (purchaseTrackingError) {
            console.error('GTM purchase tracking failed:', purchaseTrackingError);
        }

        // Clean up user selection from localStorage
        localStorage.removeItem('user_selection');

        // Check if flow=e-book, then navigate to success page
        const flow = urlParams.get('flow');
        if (flow === 'e-book') {
            window.location.href = 'https://get.easywebinar.com/success-sales-experience';
            return;
        }
        if (flow === 'agency-bundle') {
            window.location.href = 'https://get.easywebinar.com/success-agency-bundle';
            return;
        }

        navigate('/dashboard/overview?redirect=easywebinar');
    };

    // Handle message sequence
    useEffect(() => {
        let timeoutId: NodeJS.Timeout;
        let scriptLoadTimeoutId: NodeJS.Timeout;

        const showNextMessage = () => {
            setIsAnimatingOut(true);
            setTimeout(() => {
                setCurrentMessageIndex(prevIndex => {
                    const nextIndex = prevIndex + 1;
                    if (nextIndex >= loadingMessages.length) {
                        // Navigate based on SSO status
                        handleNavigation();
                        return prevIndex;
                    }
                    // Update subheading index to match the message index
                    setCurrentSubheadingIndex(nextIndex);
                    return nextIndex;
                });
                setIsAnimatingOut(false);
            }, 500);
        };

        if (currentMessageIndex === 0) {
            // First message shows for 2 seconds
            timeoutId = setTimeout(showNextMessage, 2000);
        } else if (currentMessageIndex === 1) {
            // Second message shows for at least 2 seconds and until script is loaded
            scriptLoadTimeoutId = setTimeout(() => {
                if (isScriptLoaded) {
                    showNextMessage();
                }
            }, 2000);
        } else if (currentMessageIndex === 2) {
            // Third message shows for 2 seconds
            timeoutId = setTimeout(() => {
                handleNavigation();
            }, 2000);
        }

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            if (scriptLoadTimeoutId) {
                clearTimeout(scriptLoadTimeoutId);
            }
        };
    }, [currentMessageIndex, isScriptLoaded, navigate]);

    // Set initial subheading to match first message
    useEffect(() => {
        setCurrentSubheadingIndex(0);
    }, []);

    return (
        <Container maxWidth="sm" sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Paper
                elevation={0}
                sx={{
                    width: '100%',
                    py: 6,
                    px: 4,
                    borderRadius: 2,
                    backgroundColor: 'transparent',
                    textAlign: 'center'
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    {/* Simple Spinner */}
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Box
                            component="img"
                            src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/minimal-ew-loader.gif"
                            alt="Loading..."
                            sx={{
                                width: { xs: '80px', sm: '100px', md: '120px' },
                                height: { xs: '80px', sm: '100px', md: '120px' }
                            }}
                        />
                    </Box>

                    {/* Animated loading message */}
                    <Box>
                        <Box
                            sx={{
                                height: '60px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '100%',
                            }}
                        >
                            <Typography
                                variant="h5"
                                sx={{
                                    color: 'text.secondary',
                                    animation: isAnimatingOut ? `${fadeOut} 0.5s ease forwards` : `${fadeIn} 0.5s ease forwards`,
                                    textAlign: 'center',
                                    lineHeight: 1.6,
                                    fontSize: { 
                                        xs: 'min(calc(18px + (24 - 18) * (100vw - 320px) / (1920 - 320)), 24px)', 
                                        sm: '22px', 
                                        md: '24px' 
                                    }
                                }}
                            >
                                {loadingMessages[currentMessageIndex]}
                            </Typography>
                        </Box>

                        {/* Subheading with step information */}
                        <Box
                            sx={{
                                width: '100%',
                                maxWidth: '450px',
                                textAlign: 'center',
                                position: 'relative',
                                minHeight: '60px'
                            }}
                        >
                            <Typography
                                variant="body1"
                                fontSize="min(calc(14px + (16 - 14) * (100vw - 320px) / (1920 - 320)), 16px)"
                                sx={{
                                    color: '#8a8a8a',
                                    animation: isAnimatingOut ? `${fadeOut} 0.5s ease forwards` : `${fadeIn} 0.5s ease forwards`,
                                    lineHeight: 1.5,
                                    fontWeight: 400
                                }}
                            >
                                {subheadings[currentSubheadingIndex]}
                            </Typography>
                        </Box>

                        {/* Login details email notification */}
                        <Box
                            sx={{
                                width: '100%',
                                maxWidth: '450px',
                                textAlign: 'center',
                                mt: 2
                            }}
                        >
                            <Typography
                                variant="body2"
                                fontSize="min(calc(12px + (14 - 12) * (100vw - 320px) / (1920 - 320)), 14px)"
                                sx={{
                                    color: '#6b7280',
                                    lineHeight: 1.4,
                                    fontWeight: 400,
                                    fontStyle: 'italic'
                                }}
                            >
                                Your login details have been sent to your email address
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Paper>
        </Container>
    );
};

export default LoadingDashboard;
